import { formatBankAccount } from "@edaz/utils/bankAccount";
import { bound } from "@frui.ts/helpers";
import { ConductorAllChildrenActive, Router, watchBusy } from "@frui.ts/screens";
import BusyWatcher from "@frui.ts/screens/dist/structure/busyWatcher";
import ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { action, computed, observable, reaction, when } from "mobx";
import subMinutes from "date-fns/subMinutes";
import EshopError from "models/eshopError";
import ChargeManagementService from "services/chargeManagementService";
import ChargeRegistrationService from "services/chargeRegistrationService";
import ChargeTypesService from "services/chargeTypesService";
import EnumService from "services/enumService";
import EventBus, { putChargeValiditySuccess, putChargeVehicleSuccess } from "services/eventBus";
import LocalizationService from "services/localizationService";
import { getTooltipPosition } from "../../utils";
import ChargeManagementActiveChargeLicensePlateChangeViewModel from "./chargeManagementActiveChargeLicensePlateChangeViewModel";
import ChargeManagementBeforeValidChargeLicensePlateChangeViewModel from "./chargeManagementBeforeValidChargeLicensePlateChangeViewModel";
import ChargeManagementContactViewModel from "./chargeManagementContactViewModel";
import ChargeManagementReceiptViewModel from "./chargeManagementReceiptViewModel";
import ChargeManagementValidityViewModel from "./chargeManagementValidityViewModel";
import CodeChangedAlertViewModel from "./codeChangedAlertViewModel";
import { IChargeManagementEditPartViewModel } from "./types";
import type Enum from "models/enum";
import ChargeRefundationStatus from "entities/chargeRefundationStatus";
import SourceType from "entities/sourceType";
import CustomerRefundPaymentType from "entities/customerRefundPaymentType";
import TimeService from "services/timeService";
import { dateToIsoString, isBeforePragueDay, utcToCZZonedTime } from "../../../utils/dateUtils";
import ChargeChangeRejectionReason from "entities/chargeChangeRejectionReason";

const navigationName = "edit";

const REGEX_AMOUNT = /%amount%/gi;
const REGEX_RETURNED = /%returned%/gi;
const REGEX_VARIABLE_SYMBOL = /%variable%/gi;
const REGEX_ACCOUNT = /%account%/gi;

@Router.registerRoute({ route: navigationName, name: Router.Self })
export default class ChargeManagementEditViewModel extends ConductorAllChildrenActive<
  ScreenBase & (IChargeManagementEditPartViewModel | CodeChangedAlertViewModel)
> {
  protected unsubscriptions: Array<() => void> = [];
  @observable position: "right-start" | "bottom";
  @observable country?: Enum;
  busyWatcher = new BusyWatcher();

  @computed
  get isRefunded() {
    return [ChargeRefundationStatus.Refunded, ChargeRefundationStatus.RefundedToDistributor].includes(
      this.charge?.refundationStatus!
    );
  }

  @computed
  get newChargePurchaseUrl() {
    return this.charge?.newChargePurchaseUrl?.replace("https://edalnice.cz", window.location.origin);
  }

  @computed
  get sectionsChildren() {
    return this.children.filter(child => !(child instanceof CodeChangedAlertViewModel)) as Array<
      ScreenBase & IChargeManagementEditPartViewModel
    >;
  }

  @computed
  get codeChangedAlertViewModel() {
    return this.children.find(child => child instanceof CodeChangedAlertViewModel);
  }

  @computed
  get charge() {
    return this.chargeManagementService.charge;
  }

  @computed
  get vehicle() {
    if (!this.charge || !this.charge.vehicle) return "-";

    const country = this.country;
    const licensePlate = this.charge.vehicle.licensePlate;

    return country && licensePlate ? `${country.code} – ${licensePlate}` : "-";
  }

  constructor(
    public localizationService: LocalizationService,
    private enumService: EnumService,
    protected timeService: TimeService,
    private chargeTypesService: ChargeTypesService,
    private eventBus: EventBus,
    private chargeManagementService: ChargeManagementService,
    private chargeRegistrationService: ChargeRegistrationService,
    private chargeManagementBeforeValidityChangeLicensePlateViewModel: ChargeManagementBeforeValidChargeLicensePlateChangeViewModel,
    private chargeManagementActiveChargeChangeLicensePlateViewModel: ChargeManagementActiveChargeLicensePlateChangeViewModel,
    private chargeManagementValidityViewModel: ChargeManagementValidityViewModel,
    private chargeManagementContactViewModel: ChargeManagementContactViewModel,
    private chargeManagementReceiptViewModel: ChargeManagementReceiptViewModel,
    private codeChangedAlertViewModelFactory: ReturnType<typeof CodeChangedAlertViewModel.Factory>,
    private receiptEmailModalViewModelFactory: ReturnType<typeof CodeChangedAlertViewModel.Factory>
  ) {
    super();
    this.nameValue = "chargeManagement.title";
    this.navigationName = navigationName;
    this.handleResize();
  }

  protected async onInitialize(): Promise<any> {
    await this.chargeTypesService.startInitialization(true);
    await this.timeService.getCurrentServerTime();
    this.fetchCountry();
    this.unsubscriptions.push(
      this.eventBus.subscribe(putChargeValiditySuccess, this.onCodeChanged),
      this.eventBus.subscribe(putChargeVehicleSuccess, this.onCodeChanged),
      this.eventBus.subscribe(putChargeValiditySuccess, this.onValidityChanged)
    );
    this.unsubscriptions.push(
      reaction(
        () => this.charge?.vehicle.countryId,
        () => {
          this.fetchCountry();
        }
      )
    );
    return super.onInitialize();
  }

  protected onActivate(): Promise<any[]> {
    this.children.clear();
    if (this.charge && this.charge.validSince && utcToCZZonedTime(this.charge.validSince) <= this.timeService.currentServerTime) {
      this.children.push(this.chargeManagementActiveChargeChangeLicensePlateViewModel);
    } else {
      this.children.push(this.chargeManagementBeforeValidityChangeLicensePlateViewModel);
    }

    this.children.push(
      this.chargeManagementValidityViewModel,
      this.chargeManagementContactViewModel,
      this.chargeManagementReceiptViewModel
    );
    if (this.chargeManagementService.charge) this.receiptEmailModalViewModelFactory(this.chargeManagementService.charge);

    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }

    return super.onDeactivate(isClosing);
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @bound
  private onCodeChanged() {
    if (this.chargeManagementService.charge) {
      this.children.push(this.codeChangedAlertViewModelFactory(this.chargeManagementService.charge));
    }
  }

  @bound
  private onValidityChanged(event: ReturnType<typeof putChargeValiditySuccess>) {
    // Before validity charge become valid - subMinutes is needed to deal with server vs. client clock inaccuracy
    if (
      dateToIsoString(subMinutes(utcToCZZonedTime(event.payload.validity.validSince), 1)) <=
      dateToIsoString(this.timeService.currentServerTime)
    ) {
      this.children[0] = this.chargeManagementActiveChargeChangeLicensePlateViewModel;
    }
  }

  @watchBusy
  protected async fetchCountry() {
    await when(() => this.charge !== undefined);
    this.country = await this.enumService.lazyValue("countries", this.charge?.vehicle.countryId!);
  }

  @watchBusy
  protected async fetchCharge(countryId: string, licensePlate: string, authCode: string, recaptchaToken: string) {
    return await this.chargeManagementService.getCharge(countryId, licensePlate, authCode, recaptchaToken);
  }

  setNavigationParams(countryId: string, licensePlate: string, authCode: string) {
    this.navigationParamsValue = {
      countryId,
      licensePlate,
      authCode,
    };
    this.notifyNavigationChanged();
  }

  async navigate(subPath: string, params: any) {
    if (!params) return Promise.resolve(this.back());

    const countryId = params.countryId;
    const licensePlate = params.licensePlate;
    const authCode = params.authCode;
    const recaptchaToken = this.chargeManagementService.getLoginChargeManagement() ?? "";

    if (countryId && licensePlate && authCode) {
      this.setNavigationParams(countryId, licensePlate, authCode);
    }

    if (countryId && licensePlate && authCode && !this.charge && recaptchaToken) {
      const response = await this.fetchCharge(countryId, licensePlate, authCode, recaptchaToken);
      if (response instanceof EshopError) {
        return this.back();
      }
    } else if (!this.charge) {
      return Promise.resolve(this.back());
    }

    return super.navigate(subPath, params);
  }

  @bound
  translate(code: string) {
    return this.localizationService.translateGeneral(`charge.management.edit.${code}`);
  }

  @computed
  get refundMessage() {
    if (!this.charge?.refund) return "";

    const { variableSymbol, amount, refundedAt, bankAccount, refundPaymentType } = this.charge.refund;

    const formattedBankAccount = formatBankAccount(bankAccount);

    if (refundedAt)
      return this.translate(refundPaymentType === CustomerRefundPaymentType.PayGate ? "refundPayGatePast" : "refundTransferPast")
        .replace(REGEX_AMOUNT, this.localizationService.formatPrice(amount))
        .replace(REGEX_RETURNED, this.localizationService.formatDate(refundedAt))
        .replace(REGEX_ACCOUNT, formattedBankAccount)
        .replace(REGEX_VARIABLE_SYMBOL, variableSymbol?.toString() ?? "");
    else
      return this.translate(
        refundPaymentType === CustomerRefundPaymentType.PayGate ? "refundPayGateFuture" : "refundTransferFuture"
      )
        .replace(REGEX_AMOUNT, this.localizationService.formatPrice(amount))
        .replace(REGEX_ACCOUNT, formattedBankAccount)
        .replace(REGEX_VARIABLE_SYMBOL, variableSymbol?.toString() ?? "");
  }

  @computed
  get notAllowedByOrderSourceType() {
    const isNotAllowed =
      this.charge &&
      this.charge.chargeOrderSourceType &&
      ![SourceType.Eshop, SourceType.Kiosk, SourceType.Salespoint].includes(this.charge.chargeOrderSourceType);

    return isNotAllowed;
  }

  @computed
  get afterValidityStart() {
    return (
      this.charge &&
      !this.charge.permittedActions.validityChange.isAllowed &&
      this.charge.permittedActions.validityChange.rejectionReason === ChargeChangeRejectionReason.InvalidChargeValidityStatus
    );
  }
  @computed
  get rejectionReasonEmailAddressChange() {
    if (this.charge && !this.charge.permittedActions.emailAddressChange.isAllowed) {
      return this.charge.permittedActions.emailAddressChange.rejectionReason;
    }
  }

  @computed
  get rejectionReasonContact() {
    if (
      this.charge &&
      !this.charge.permittedActions.emailNotificationPreferencesChange.isAllowed &&
      !this.charge.permittedActions.smsNotificationPreferencesChange.isAllowed &&
      !this.charge.permittedActions.emailAddressChange.isAllowed
    ) {
      return this.charge.permittedActions.emailAddressChange.rejectionReason;
    }
    if (this.charge && !this.charge.permittedActions.emailNotificationPreferencesChange.isAllowed) {
      return this.charge.permittedActions.emailNotificationPreferencesChange.rejectionReason;
    }
    if (this.charge && !this.charge.permittedActions.smsNotificationPreferencesChange.isAllowed) {
      return this.charge.permittedActions.smsNotificationPreferencesChange.rejectionReason;
    }
  }
  @computed
  get rejectionReasonObtainingPaymentConfirmation() {
    if (this.charge && !this.charge.permittedActions.obtainingPaymentConfirmation.isAllowed) {
      return this.charge.permittedActions.obtainingPaymentConfirmation.rejectionReason;
    }
  }
  @computed
  get rejectionReasonSmsNotificationPreferencesChange() {
    if (this.charge && !this.charge.permittedActions.smsNotificationPreferencesChange.isAllowed) {
      return this.charge.permittedActions.smsNotificationPreferencesChange.rejectionReason;
    }
  }
  @computed
  get rejectionReasonValidityChange() {
    if (this.charge && !this.charge.permittedActions.validityChange.isAllowed) {
      return this.charge.permittedActions.validityChange.rejectionReason;
    }
  }
  @computed
  get rejectionReasonVehicleChangeBeforeValidity() {
    if (this.charge && !this.charge.permittedActions.vehicleChangeBeforeValidity.isAllowed) {
      return this.charge.permittedActions.vehicleChangeBeforeValidity.rejectionReason;
    }
  }
  @computed
  get rejectionReasonVehicleChangeDuringValidity() {
    if (this.charge && !this.charge.permittedActions.vehicleChangeDuringValidity.isAllowed) {
      return this.charge.permittedActions.vehicleChangeDuringValidity.rejectionReason;
    }
  }

  @computed
  get getChargeType() {
    const chargeType = this.charge
      ? this.chargeTypesService.getPriceListItemChargeType(this.charge.priceListItemId, true)
      : undefined;
    return chargeType?.description;
  }

  @computed
  get afterValidity() {
    return this.charge && isBeforePragueDay(this.charge.validUntil, this.timeService.currentServerTime);
  }

  @computed
  get licensePlate() {
    if (!this.charge || !this.charge.vehicle) return "-";

    const licensePlate = this.charge.vehicle.licensePlate;

    return licensePlate ? licensePlate : "-";
  }

  @computed
  get validSince() {
    if (!this.charge || !this.charge.validSince) return "-";

    const validSince = this.charge.validSince;

    return validSince ? validSince : "-";
  }

  @computed
  get validUntil() {
    if (!this.charge || !this.charge.validUntil) return "-";

    const validUntil = this.charge.validUntil;

    return validUntil ? validUntil : "-";
  }

  @computed
  get drive() {
    if (!this.charge || !this.charge.priceListItemId) {
      return "-";
    }

    const fuelType = this.chargeTypesService.getPriceListItemFuelType(this.charge.priceListItemId, true);
    return fuelType ? this.localizationService.translateAttribute("fuelType", fuelType) : "-";
  }

  @computed
  get authCode() {
    if (!this.charge) return "-";

    const authCode = this.charge?.authCode;

    return authCode ? authCode : "-";
  }

  @computed
  get chargeTypeWithPrice() {
    if (!this.charge || !this.charge.priceListItemId) {
      return "-";
    }

    const chargeType = this.chargeTypesService.getPriceListItemChargeType(this.charge.priceListItemId, true);
    if (!chargeType) {
      return "-";
    }

    const price = this.chargeTypesService.getPrice(this.charge.priceListItemId, true);
    if (!price) {
      return chargeType.description;
    }

    return `${chargeType.description} – ${this.localizationService.formatPrice(price)}`;
  }

  @computed
  get registrationCountry() {
    return this.country ? this.country.name : "-";
  }

  @bound
  handleResize() {
    this.position = getTooltipPosition(window);
  }

  @action.bound
  back() {
    this.chargeManagementService.reset();
    this.chargeRegistrationService.reset();
    this.requestClose();
  }
}
